import { collection, doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import { RBAC_ASSIGNMENTS_COLLECTION } from "@/features/rbac/services/assignmentsService";
import { isStaffEmail } from "@/features/staff/schemas/staff";
import {
  createStaffMember,
  getStaffMember,
} from "@/features/staff/services/staffService";

/**
 * Migración única de las cuentas de equipo desde `users` hacia `staff`.
 *
 * Se ejecuta MIENTRAS el gate antiguo sigue disponible, para poder revisar la
 * lista y comprobar que no falta nadie antes de que la puerta de entrada pase a
 * exigir perfil de equipo. Ese orden es lo que evita que alguien se quede fuera.
 */

/** Una cuenta candidata a migrar, con su diagnóstico. */
export interface StaffMigrationCandidate {
  uid: string;
  email: string;
  name: string;
  /** Roles heredados de `permission_admin`, que se conservan al migrar. */
  legacyPermissions: string[];
  /** El correo pertenece al dominio corporativo. */
  hasCorporateEmail: boolean;
  /** Ya tiene documento en `staff`. */
  alreadyMigrated: boolean;
  /**
   * Se puede migrar tal cual. Falso cuando el correo no es corporativo: esa
   * persona necesita una cuenta nueva bajo el dominio, no una migración.
   */
  migratable: boolean;
}

function displayNameOf(data: Record<string, unknown>): string {
  const name = typeof data.name === "string" ? data.name.trim() : "";
  if (name) return name;

  const forename = typeof data.forename === "string" ? data.forename : "";
  const surname = typeof data.surname === "string" ? data.surname : "";
  const composed = [forename, surname].filter(Boolean).join(" ").trim();

  return composed || (typeof data.email === "string" ? data.email : "");
}

function legacyPermissionsOf(data: Record<string, unknown>): string[] {
  const permissions = Array.isArray(data.permission_admin) ? data.permission_admin : [];
  const roleIds = permissions.filter(
    (value): value is string => typeof value === "string" && value.length > 0
  );

  // `type: "admin"` concedía acceso total sin figurar en permission_admin.
  if (data.type === "admin" && !roleIds.includes("admin")) roleIds.push("admin");

  return [...new Set(roleIds)].sort();
}

/**
 * Lista las cuentas con acceso al panel según el gate antiguo.
 *
 * Devuelve TODAS, incluidas las que no se pueden migrar por no tener correo
 * corporativo: verlas es justamente el objetivo, porque son las que se
 * quedarían fuera cuando el gate deje de aceptar la vía antigua.
 */
export async function findLegacyAdminAccounts(): Promise<StaffMigrationCandidate[]> {
  const snapshot = await getDocs(
    query(collection(db, "users"), where("vm_admin", "==", true))
  );

  const candidates = await Promise.all(
    snapshot.docs.map(async (docSnap) => {
      const data = docSnap.data();
      const email = typeof data.email === "string" ? data.email : "";
      const hasCorporateEmail = isStaffEmail(email);
      const alreadyMigrated = (await getStaffMember(docSnap.id)) !== null;

      return {
        uid: docSnap.id,
        email,
        name: displayNameOf(data),
        legacyPermissions: legacyPermissionsOf(data),
        hasCorporateEmail,
        alreadyMigrated,
        migratable: hasCorporateEmail && !alreadyMigrated,
      } satisfies StaffMigrationCandidate;
    })
  );

  return candidates.sort((a, b) => a.name.localeCompare(b.name, "es"));
}

export interface StaffMigrationResult {
  migrated: string[];
  failed: { email: string; reason: string }[];
}

/**
 * Congela los permisos heredados como asignación explícita de roles.
 *
 * Es IMPRESCINDIBLE hacerlo al migrar: una cuenta de equipo ya no arrastra
 * `permission_admin`, así que sin este paso la persona se quedaría con cero
 * permisos justo después de migrarse. Y como nadie puede asignarse roles a sí
 * mismo, quien migrara primero se autobloquearía.
 *
 * Por eso escribe directo y no pasa por `setAssignment`: no está concediendo
 * permisos nuevos, está preservando los que la persona ya tenía.
 */
async function freezeLegacyRoles(
  candidate: StaffMigrationCandidate,
  actorUid: string | null
): Promise<void> {
  if (candidate.legacyPermissions.length === 0) return;

  const existing = await getDoc(
    doc(db, RBAC_ASSIGNMENTS_COLLECTION, candidate.uid)
  );
  // Si ya tenía asignación explícita, esa manda: no se pisa.
  if (existing.exists()) return;

  await setDoc(doc(db, RBAC_ASSIGNMENTS_COLLECTION, candidate.uid), {
    subject_uid: candidate.uid,
    subject_type: "staff",
    subject_email: candidate.email,
    role_ids: candidate.legacyPermissions,
    updated_at: new Date(),
    updated_by: actorUid,
  });
}

/**
 * Crea el documento `staff` de las cuentas indicadas y conserva sus permisos.
 *
 * Mantiene el mismo `uid`, así que la persona entra con la misma credencial de
 * Firebase Auth y sus asignaciones siguen apuntando a ella.
 */
export async function migrateAccountsToStaff(
  candidates: readonly StaffMigrationCandidate[],
  actorUid: string | null
): Promise<StaffMigrationResult> {
  const result: StaffMigrationResult = { migrated: [], failed: [] };

  for (const candidate of candidates) {
    if (!candidate.migratable) {
      result.failed.push({
        email: candidate.email,
        reason: candidate.alreadyMigrated
          ? "Ya formaba parte del equipo"
          : "El correo no pertenece al dominio corporativo",
      });
      continue;
    }

    try {
      // Los roles primero: si fallara la creación del perfil, la persona sigue
      // entrando por la vía antigua con sus permisos intactos.
      await freezeLegacyRoles(candidate, actorUid);

      await createStaffMember(
        {
          uid: candidate.uid,
          email: candidate.email,
          name: candidate.name,
          migratedFromUserUid: candidate.uid,
        },
        actorUid
      );

      result.migrated.push(candidate.email);
    } catch (error) {
      result.failed.push({
        email: candidate.email,
        reason: error instanceof Error ? error.message : "Error desconocido",
      });
    }
  }

  return result;
}
